import { Component, inject, OnInit, signal } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { KcIconComponent } from '@kaspacom/ui-kit';
import { SkeletonComponent } from '../../../../shared/ui/skeleton/skeleton.component';
import { CopyButtonComponent } from '../../../../shared/ui/copy-button/copy-button.component';
import { KasplexKrc20Service } from '../../../../../services/kasplex-api/kasplex-api.service';
import { OperationDetails } from '../../../../../services/kasplex-api/dtos/operation-details-response';
import { KaspaNetworkActionsService } from '../../../../../services/kaspa-netwrok-services/kaspa-network-actions.service';
import { KaspaL1NetworkService } from '../../../../../services/kaspa-netwrok-services/kaspa-l1-network.service';

@Component({
  selector: 'app-krc20-transaction-details',
  imports: [KcIconComponent, SkeletonComponent, CopyButtonComponent],
  templateUrl: './krc20-transaction-details.component.html',
  styleUrl: './krc20-transaction-details.component.scss',
})
export class Krc20TransactionDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private kasplexService = inject(KasplexKrc20Service);
  private kaspaNetworkActionsService = inject(KaspaNetworkActionsService);
  private kaspaL1NetworkService = inject(KaspaL1NetworkService);

  protected ticker: string | null = null;
  protected transactionId: string | null = null;
  protected operationDetails = signal<OperationDetails | null>(null);
  protected loading = signal<boolean>(true);

  ngOnInit(): void {
    this.ticker = this.route.snapshot.paramMap.get('ticker');
    this.transactionId = this.route.snapshot.paramMap.get('transactionId');

    if (this.transactionId) {
      this.loadOperationDetails();
    } else {
      this.loading.set(false);
    }
  }

  private async loadOperationDetails(): Promise<void> {
    if (!this.transactionId) return;

    try {
      this.loading.set(true);
      const response = await firstValueFrom(
        this.kasplexService.getOperationDetails(this.transactionId),
      );

      if (response.message === 'successful' && response.result?.length > 0) {
        this.operationDetails.set(response.result[0]);
      }
    } catch (error) {
      console.error('Failed to load operation details:', error);
    } finally {
      this.loading.set(false);
    }
  }

  protected goBack(): void {
    // Check if we came from activity page
    const navigationState = this.router.getCurrentNavigation()?.extras?.state;
    const historyState = history.state;

    const returnTo =
      navigationState?.['returnTo'] || historyState?.['returnTo'];

    if (returnTo === 'activity') {
      this.router.navigate(['/app/activity']);
    } else if (this.ticker) {
      this.router.navigate(['/app/home/asset/krc20', this.ticker]);
    } else {
      this.router.navigate(['/app/home'], { queryParams: { tab: 'krc20' } });
    }
  }

  protected formatAmount(amount: string | undefined): string {
    if (!amount) return '0';
    const amountInSompi = BigInt(amount);
    const numAmount =
      this.kaspaNetworkActionsService.sompiToNumber(amountInSompi);
    return numAmount.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 8,
    });
  }

  protected formatDate(timestamp: string): string {
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }

  protected shortenAddress(address: string): string {
    if (!address) return '';
    return `${address.slice(0, 10)}...${address.slice(-8)}`;
  }

  protected getOperationStatus(operation: OperationDetails): string {
    if (operation.txAccept === '1' && operation.opAccept === '1') {
      return 'Accepted';
    } else if (operation.txAccept === '1' && operation.opAccept !== '1') {
      return 'Implemented';
    } else {
      return 'Pending';
    }
  }

  protected isOperationAccepted(operation: OperationDetails): boolean {
    return operation.txAccept === '1' && operation.opAccept === '1';
  }

  protected openTransactionInExplorer(): void {
    const operation = this.operationDetails();
    if (operation) {
      const explorerUrl = `${this.kaspaL1NetworkService.getKaspaExplorerBaseurl()}/txs/${operation.hashRev}`;
      window.open(explorerUrl, '_blank');
    }
  }
}
